import React, { useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import Input from 'components/atoms/Input'
import Button from 'components/atoms/Button'
import {
  changeQuery,
  getCurrentParsedQuery,
  stringifyQuery
} from 'utils/navigation'
import { isNotNilOrEmpty } from 'utils/ramda'
import { dissoc, propOr } from 'ramda'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { fetchZonesRoutine } from 'modules/zones/ducks/actions'
import { selectZonesList } from 'modules/zones/ducks/selectors'
import { fetchZonesDictionaryRoutine } from 'ducks/dictionaries/actions'
import { selectZonesDictionary } from 'ducks/dictionaries/selectors'
import {
  validateFiltersField,
  validateFiltersValues
} from 'modules/locations/ducks/schema'
import { ButtonsWrapper } from 'theme/wrappers'

const emptyValues = {
  zone: 'all',
  zone_type: 'all'
}

const LocationsFilters = ({ open }) => {
  const [values, setValues] = useState(emptyValues)
  const [valid, setValid] = useState(false)
  const {
    location: { search }
  } = useHistory()
  const dispatch = useDispatch()
  const zonesList = useSelector(selectZonesList)
  const zonesDictionary = useSelector(selectZonesDictionary)
  const zoneTypes = propOr({}, 'zoneTypes', zonesDictionary)

  const resetFilters = () => {
    setValues(emptyValues)
    changeQuery({
      page: 1,
      sort: 'name'
    })
  }

  useEffect(() => {
    dispatch(
      fetchZonesRoutine({
        query: stringifyQuery({
          page: 1,
          limit: 1000
        })
      })
    )
    dispatch(fetchZonesDictionaryRoutine())
  }, [])

  useEffect(() => {
    const currentQuery = getCurrentParsedQuery()
    setValues({
      ...emptyValues,
      ...propOr({}, 'filter', currentQuery)
    })
  }, [search])

  const handleValueChange = (name, value) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }

  const zonesOptions = useMemo(() => {
    if (isNotNilOrEmpty(zonesList)) {
      return [
        { label: 'Wszystkie', value: 'all' },
        ...zonesList.map(type => ({
          label: type.name,
          value: type.id
        }))
      ]
    } else {
      return [{ label: 'Wszystkie', value: 'all' }]
    }
  }, [zonesList])

  const zoneTypesOptions = useMemo(() => {
    let options = [{ label: 'Wszystkie', value: 'all' }]
    for (let key in zoneTypes) {
      options.push({ label: zoneTypes[key], value: key })
    }
    return options
  }, [zoneTypes])

  const handleSubmit = () => {
    const currentQuery = getCurrentParsedQuery()
    let payload = {}

    for (let key in values) {
      if (isNotNilOrEmpty(values[key]) && values[key] !== 'all') {
        payload[key] = values[key]
      } else {
        payload = dissoc(key, payload)
      }
    }

    changeQuery({
      ...currentQuery,
      page: 1,
      filter: payload
    })
  }

  useEffect(() => {
    validateFiltersValues(values, setValid)
  }, [values])

  return (
    <Wrapper open={open}>
      <Input
        select
        options={zonesOptions}
        value={values.zone}
        name='zone'
        label='Strefa'
        onChange={handleValueChange}
        validate={validateFiltersField(values)}
        noMarginBottom
      />
      <Input
        select
        options={zoneTypesOptions}
        value={values.zone_type}
        name='zone_type'
        label='Typ strefy'
        onChange={handleValueChange}
        validate={validateFiltersField(values)}
        noMarginBottom
      />
      <ButtonsWrapper>
        <Button small onClick={handleSubmit} color='primary' disabled={!valid}>
          Zastosuj
        </Button>
        <Button small onClick={resetFilters} color='primary-outline'>
          Resetuj
        </Button>
      </ButtonsWrapper>
    </Wrapper>
  )
}

export default LocationsFilters

const Wrapper = styled.div`
  background-color: #fff;
  border-radius: 4px;
  box-shadow: ${({ theme }) => theme.shadows.main};
  width: 100%;
  overflow: hidden;
  height: 0;
  padding: 0 20px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 15px;

  ${({ open }) =>
    open &&
    css`
      padding: 20px;
      margin-bottom: 20px;
      height: 110px;
    `}
`
